import { useCallback, useMemo } from 'react';
import { getSupplyLabel, getSupplyTerm, isSupportedDurationKey, toPerMonthRate } from '../../utils/supplyTerms';
import styles from './SupplyMonthSelector.module.css';

const DEFAULT_SUPPLY_OPTIONS = [1, 2, 3];
// Resolve price from either flat number (legacy) or tiered object {access, one, plus}.
// `tierType` is the tier the SERVER priced the product for; omitting it keeps the
// original access-first chain, so callers that don't pass it are unaffected.
const resolvePrice = (price, tierType = 'access') => {
  if (typeof price === 'number') return price;
  if (price && typeof price === 'object') {
    return price[tierType] ?? price.access ?? price.one ?? price.plus ?? 0;
  }
  return 0;
};


const SupplyMonthSelector = ({
  pricePerMonth,
  supplyPricing = null,
  supplyPricingEffective = null,
  supplyPricingMeta = null,
  selectedMonths = 1,
  tierType = null,
  hasTierPriceModifier = false,
  onChange,
  availableOptions = null,
  className = ''
}) => {
  const handleChange = useCallback((e) => {
    const months = parseInt(e.target.value, 10);
    onChange?.(months);
  }, [onChange]);

  // Use the server's modifier-applied effective map ONLY for a viewer who actually carries a
  // tier modifier -- the caller decides, from backend data. The effective map is built from the
  // viewer-agnostic per-tier modifier map, so it bakes in Access's +10% for everyone; preferring
  // it whenever present over-quoted every no-tier and signed-out viewer, who is charged the raw
  // rate. Callers that pass only the raw/promo-adjusted `supplyPricing` (the three offer-page
  // call sites) pass no effective map at all, so the default `false` is a no-op for them.
  const effectiveSource = hasTierPriceModifier
    && supplyPricingEffective
    && typeof supplyPricingEffective === 'object'
    && Object.keys(supplyPricingEffective).length > 0
    ? supplyPricingEffective
    : null;
  const priceSource = effectiveSource || supplyPricing;

  const formatPrice = (cents) => {
    if (!cents && cents !== 0) return '$0';
    // Effective (charge-exact) rates can carry cents (e.g. $86.90) — show them so the term
    // equals the charge. The legacy/promo path keeps whole-dollar rounding (offer pages).
    if (effectiveSource) return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
    return `$${(cents / 100).toFixed(0)}`;
  };

  // Derive pricing tiers from supplyPricing or fall back to flat pricePerMonth
  const pricingTiers = useMemo(() => {
    if (priceSource && typeof priceSource === 'object' && Object.keys(priceSource).length > 0) {
      const meta = supplyPricingMeta || {};
      return Object.entries(priceSource)
        .filter(([months]) => {
          // Strict key policy: this component reads the raw supplyPricing keys
          // rather than going through the supply-term engine, so it has to
          // refuse an off-map key itself — otherwise a data defect renders as a
          // real, selectable term. See web/utils/supplyTerms.js.
          if (!isSupportedDurationKey(months)) return false;
          const m = meta[months];
          return m?.enabled !== false; // only show enabled durations
        })
        .map(([months, price]) => {
          // `price` is charged per raw key unit; `rate` is the same money expressed per
          // internal month. They differ for key "1" alone — see toPerMonthRate.
          const charged = resolvePrice(price, tierType || 'access');
          return {
            months: parseInt(months, 10),
            price: charged,
            rate: toPerMonthRate(charged, months),
            internalMonths: getSupplyTerm(months).internalMonths,
            label: meta[months]?.label
          };
        })
        .sort((a, b) => a.months - b.months);
    }
    return null;
  }, [priceSource, supplyPricingMeta, tierType]);

  const options = pricingTiers
    ? pricingTiers.map(t => t.months)
    : (availableOptions || DEFAULT_SUPPLY_OPTIONS);

  const findTier = (months) => pricingTiers?.find(t => t.months === months) ?? null;

  // Charge-exact rate, per raw key unit — the only one the total is built from.
  const getMonthlyPrice = (months) => findTier(months)?.price ?? pricePerMonth;
  // Display rate and the number of months it is quoted per. The legacy flat path has no
  // supply term, so its months stay literal.
  const getDisplayRate = (months) => findTier(months)?.rate ?? pricePerMonth;
  const getDisplayMonths = (months) => findTier(months)?.internalMonths ?? months;

  const currentMonthlyPrice = getMonthlyPrice(selectedMonths);
  const totalPrice = currentMonthlyPrice * selectedMonths;
  const currentRate = getDisplayRate(selectedMonths);
  const currentMonths = getDisplayMonths(selectedMonths);

  // Savings vs the base term (duration key "1" — the 60 Day tier), compared per INTERNAL
  // month. Comparing the stored values instead measured 60 Day at twice its true rate and
  // overstated every badge on the page against that inflated baseline.
  const baseMonthlyPrice = pricingTiers
    ? (findTier(1)?.rate || pricingTiers[0]?.rate || pricePerMonth)
    : pricePerMonth;
  const savingsPercent = baseMonthlyPrice > 0 && currentRate < baseMonthlyPrice
    ? Math.round(((baseMonthlyPrice - currentRate) / baseMonthlyPrice) * 100)
    : 0;

  return (
    <div className={`${styles.container} ${className}`}>
      <div className={styles.selectorRow}>
        <div className={styles.dropdownWrapper}>
          <select
            className={styles.dropdown}
            value={selectedMonths}
            onChange={handleChange}
            aria-label="Select supply duration"
          >
            {options.map(months => {
              const monthRate = getDisplayRate(months);
              const moSavings = baseMonthlyPrice > 0 && monthRate < baseMonthlyPrice
                ? Math.round(((baseMonthlyPrice - monthRate) / baseMonthlyPrice) * 100)
                : 0;
              const tierData = findTier(months);
              const termLabel = tierData?.label || getSupplyLabel(months);
              const label = pricingTiers
                ? `${termLabel} \u2014 ${formatPrice(monthRate)}/mo${moSavings > 0 ? ` (${moSavings}% off)` : ''}`
                : termLabel;
              return (
                <option key={months} value={months}>
                  {label}
                </option>
              );
            })}
          </select>
          <svg
            className={styles.dropdownArrow}
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        <span className={styles.supplyLabel}>supply</span>
      </div>
      <div className={styles.priceBreakdown}>
        <span className={styles.calculation} data-testid="supply-term-quote">
          {formatPrice(currentRate)}/mo &times; {currentMonths} = <strong>{formatPrice(totalPrice)}</strong>
          {savingsPercent > 0 && (
            <span className={styles.savings}> &mdash; save {savingsPercent}%</span>
          )}
        </span>
      </div>
    </div>
  );
};

export default SupplyMonthSelector;
